import { NormalizedExpression } from '../NormalizedExpression';
import type { AttributeCriterion, InnerRule, Value } from './types';

/**
 * Criterion from Segment targeting
 */
type Criterion = {
  attribute?: AttributeCriterion;
  segment?: {
    segment: string;
  };
};

/**
 * Targeting expression from Segment (simplified structure with refs only)
 */
type TargetingExpression = {
  ref?: string;
  not?: TargetingExpression;
  and?: {
    operands?: TargetingExpression[];
  };
  or?: {
    operands?: TargetingExpression[];
  };
};

/**
 * Segment targeting structure
 */
type SegmentTargeting = {
  expression?: TargetingExpression;
  criteria?: {
    [key: string]: Criterion;
  };
};

/**
 * Type helper to extract Value type from NormalizedExpression types
 */
type NormalizedValue = NormalizedExpression.BinaryExpression['value'];

/**
 * Converts a local Value type to NormalizedExpression.Value type
 */
const convertValue = (value: Value): NormalizedValue => {
  if (value.stringValue !== undefined) {
    return value.stringValue;
  }
  if (value.numberValue !== undefined) {
    return value.numberValue;
  }
  if (value.boolValue !== undefined) {
    return value.boolValue;
  }
  if (value.timestampValue !== undefined) {
    return value.timestampValue;
  }
  if (value.versionValue !== undefined) {
    return value.versionValue;
  }
  return null;
};

/**
 * Converts an array of local Value types to NormalizedExpression.Value types
 */
const convertValues = (values: Value[]): NormalizedValue[] => {
  return values.map(convertValue);
};

/**
 * Checks if a Value represents null (all fields are undefined)
 */
export const isNullValue = (value: Value): boolean => {
  return Object.keys(value).length === 0;
};

/**
 * Converts a Value to a string representation for display
 */
export const valueToString = (value: Value): string => {
  return NormalizedExpression.valueToString(convertValue(value));
};

/**
 * Normalizes an inner rule (without attributeName) to an InnerExpression
 * Used for anyRule and allRule
 */
const normalizeInnerRule = (
  rule: InnerRule,
  isNegated: boolean = false,
): NormalizedExpression.InnerExpression | null => {
  // Handle eqRule (equals/not equals)
  if (rule.eqRule) {
    const value = rule.eqRule.value;

    // Check if this is a null check - but inner rules don't support unary operators
    // So we'll treat null as a regular value
    if (isNullValue(value)) {
      // For inner rules, null values are treated as regular binary comparisons
      const operator: NormalizedExpression.BinaryOperator = isNegated
        ? '!='
        : '=';
      return {
        type: 'binary',
        operator,
        value: convertValue(value),
      };
    }

    // Regular equality check (binary operator)
    const operator: NormalizedExpression.BinaryOperator = isNegated
      ? '!='
      : '=';
    return {
      type: 'binary',
      operator,
      value: convertValue(value),
    };
  }

  // Handle setRule (any-of/none-of)
  if (rule.setRule) {
    const operator: NormalizedExpression.SetOperator = isNegated
      ? 'none-of'
      : 'any-of';
    return {
      type: 'set',
      operator,
      values: convertValues(rule.setRule.values),
    };
  }

  // Handle rangeRule
  if (rule.rangeRule) {
    const { rangeRule } = rule;
    const start = rangeRule.startInclusive ?? rangeRule.startExclusive;
    const end = rangeRule.endInclusive ?? rangeRule.endExclusive;

    if (!start || !end) {
      // Handle open-ended ranges as binary comparisons
      if (start) {
        const operator: NormalizedExpression.BinaryOperator =
          rangeRule.startInclusive ? '>=' : '>';
        return {
          type: 'binary',
          operator,
          value: convertValue(start),
        };
      }
      if (end) {
        const operator: NormalizedExpression.BinaryOperator =
          rangeRule.endInclusive ? '<=' : '<';
        return {
          type: 'binary',
          operator,
          value: convertValue(end),
        };
      }
      return null;
    }

    // Closed range
    const range: NormalizedExpression.RangeSpec = {
      start: convertValue(start),
      end: convertValue(end),
      startInclusive: !!rangeRule.startInclusive,
      endInclusive: !!rangeRule.endInclusive,
    };

    return {
      type: 'range',
      operator: isNegated ? 'not-between' : 'between',
      range,
    };
  }

  return null;
};

/**
 * Normalizes an AttributeCriterion to a normalized expression
 */
export const normalizeAttributeCriterion = (
  criterion: AttributeCriterion,
  isNegated: boolean = false,
): NormalizedExpression.Expression | null => {
  const { attributeName } = criterion;

  // Handle eqRule (equals/not equals)
  if (criterion.eqRule) {
    const value = criterion.eqRule.value;

    // Check if this is a null check (unary operator)
    if (isNullValue(value)) {
      const operator: NormalizedExpression.UnaryOperator = isNegated
        ? 'is-not-null'
        : 'is-null';
      return {
        type: 'unary',
        attributeName,
        operator,
      };
    }

    // Regular equality check (binary operator)
    const operator: NormalizedExpression.BinaryOperator = isNegated
      ? '!='
      : '=';
    return {
      type: 'binary',
      attributeName,
      operator,
      value: convertValue(value),
    };
  }

  // Handle setRule (any-of/none-of)
  if (criterion.setRule) {
    const operator: NormalizedExpression.SetOperator = isNegated
      ? 'none-of'
      : 'any-of';
    return {
      type: 'set',
      attributeName,
      operator,
      values: convertValues(criterion.setRule.values),
    };
  }

  // Handle rangeRule
  if (criterion.rangeRule) {
    const { rangeRule } = criterion;
    const start = rangeRule.startInclusive ?? rangeRule.startExclusive;
    const end = rangeRule.endInclusive ?? rangeRule.endExclusive;

    if (!start || !end) {
      // Handle open-ended ranges as binary comparisons
      if (start) {
        const operator: NormalizedExpression.BinaryOperator =
          rangeRule.startInclusive ? '>=' : '>';
        return {
          type: 'binary',
          attributeName,
          operator,
          value: convertValue(start),
        };
      }
      if (end) {
        const operator: NormalizedExpression.BinaryOperator =
          rangeRule.endInclusive ? '<=' : '<';
        return {
          type: 'binary',
          attributeName,
          operator,
          value: convertValue(end),
        };
      }
      return null;
    }

    // Closed range
    const range: NormalizedExpression.RangeSpec = {
      start: convertValue(start),
      end: convertValue(end),
      startInclusive: !!rangeRule.startInclusive,
      endInclusive: !!rangeRule.endInclusive,
    };

    return {
      type: 'range',
      attributeName,
      operator: isNegated ? 'not-between' : 'between',
      range,
    };
  }

  // Handle anyRule (nested expression with ANY semantics)
  if (criterion.anyRule?.rule) {
    const rule = criterion.anyRule.rule;
    const innerExpression = normalizeInnerRule(rule, isNegated);
    if (!innerExpression) {
      return null;
    }
    return {
      type: 'array',
      attributeName,
      operator: 'match-any',
      rule: innerExpression,
    };
  }

  // Handle allRule (nested expression with ALL semantics)
  if (criterion.allRule?.rule) {
    const rule = criterion.allRule.rule;
    const innerExpression = normalizeInnerRule(rule, isNegated);
    if (!innerExpression) {
      return null;
    }
    return {
      type: 'array',
      attributeName,
      operator: 'match-all',
      rule: innerExpression,
    };
  }

  return null;
};

/**
 * Normalizes a SegmentCriterion to a normalized expression
 */
export const normalizeSegmentCriterion = (
  criterion: { segment: string },
  isNegated: boolean = false,
): NormalizedExpression.SegmentExpression => {
  const operator: NormalizedExpression.SegmentOperator = isNegated
    ? 'not-in-segment'
    : 'in-segment';
  return {
    type: 'segment',
    operator,
    segmentId: criterion.segment,
  };
};

/**
 * Normalizes criteria map to an array of normalized expressions
 * Each criterion is normalized independently
 */
export const normalizeCriteria = (
  criteria: { [key: string]: Criterion } | null | undefined,
): NormalizedExpression.Expression[] => {
  if (!criteria) {
    return [];
  }

  return Object.values(criteria)
    .map(criterion => {
      if (criterion?.attribute) {
        return normalizeAttributeCriterion(criterion.attribute, false);
      }
      if (criterion?.segment) {
        return normalizeSegmentCriterion(criterion.segment, false);
      }
      return null;
    })
    .filter((expr): expr is NormalizedExpression.Expression => expr !== null);
};

/**
 * Normalizes a TargetingExpression using criteria to resolve refs
 */
const normalizeTargetingExpression = (
  expression: TargetingExpression,
  isNegated: boolean = false,
  criteria?: { [key: string]: Criterion },
): NormalizedExpression.Expression | null => {
  // Handle reference to a criterion
  if (expression.ref) {
    const criterion = criteria?.[expression.ref];
    if (criterion?.attribute) {
      return normalizeAttributeCriterion(criterion.attribute, isNegated);
    }
    if (criterion?.segment) {
      return normalizeSegmentCriterion(criterion.segment, isNegated);
    }
    return null;
  }

  // Handle NOT
  if (expression.not) {
    // Handle NOT with a ref
    if (expression.not.ref) {
      const criterion = criteria?.[expression.not.ref];
      if (criterion?.attribute) {
        return normalizeAttributeCriterion(criterion.attribute, !isNegated);
      }
      if (criterion?.segment) {
        return normalizeSegmentCriterion(criterion.segment, !isNegated);
      }
      return null;
    }

    // Handle NOT with nested expression (recursively)
    return normalizeTargetingExpression(expression.not, !isNegated, criteria);
  }

  // Handle AND
  if (expression.and?.operands) {
    const operands = expression.and.operands
      .map(operand => {
        // Handle nested expressions recursively
        if (operand.ref) {
          const criterion = criteria?.[operand.ref];
          if (criterion?.attribute) {
            return normalizeAttributeCriterion(criterion.attribute, isNegated);
          }
          if (criterion?.segment) {
            return normalizeSegmentCriterion(criterion.segment, isNegated);
          }
          return null;
        }

        // Handle nested NOT expression
        if (operand.not) {
          return normalizeTargetingExpression(
            { not: operand.not },
            isNegated,
            criteria,
          );
        }

        // Handle nested AND expression
        if (operand.and) {
          return normalizeTargetingExpression(
            { and: operand.and },
            isNegated,
            criteria,
          );
        }

        // Handle nested OR expression
        if (operand.or) {
          return normalizeTargetingExpression(
            { or: operand.or },
            isNegated,
            criteria,
          );
        }

        return null;
      })
      .filter((expr): expr is NormalizedExpression.Expression => expr !== null);

    if (operands.length === 0) {
      return null;
    }

    if (operands.length === 1) {
      return operands[0];
    }

    return {
      type: 'logical',
      operator: isNegated ? 'or' : 'and',
      operands,
    };
  }

  // Handle OR
  if (expression.or?.operands) {
    const operands = expression.or.operands
      .map(operand => {
        // Handle nested expressions recursively
        if (operand.ref) {
          const criterion = criteria?.[operand.ref];
          if (criterion?.attribute) {
            return normalizeAttributeCriterion(criterion.attribute, isNegated);
          }
          if (criterion?.segment) {
            return normalizeSegmentCriterion(criterion.segment, isNegated);
          }
          return null;
        }

        // Handle nested NOT expression
        if (operand.not) {
          return normalizeTargetingExpression(
            { not: operand.not },
            isNegated,
            criteria,
          );
        }

        // Handle nested AND expression
        if (operand.and) {
          return normalizeTargetingExpression(
            { and: operand.and },
            isNegated,
            criteria,
          );
        }

        // Handle nested OR expression
        if (operand.or) {
          return normalizeTargetingExpression(
            { or: operand.or },
            isNegated,
            criteria,
          );
        }

        return null;
      })
      .filter((expr): expr is NormalizedExpression.Expression => expr !== null);

    if (operands.length === 0) {
      return null;
    }

    if (operands.length === 1) {
      return operands[0];
    }

    return {
      type: 'logical',
      operator: isNegated ? 'and' : 'or',
      operands,
    };
  }

  return null;
};

/**
 * Normalizes Segment.targeting to a normalized expression
 * Uses the expression tree if present, otherwise falls back to all criteria
 */
export const normalizeTargeting = (
  targeting: SegmentTargeting | null | undefined,
): NormalizedExpression.Expression | null => {
  if (!targeting) {
    return null;
  }

  // Normalize expression if present (this respects the logical structure)
  if (targeting.expression) {
    return normalizeTargetingExpression(
      targeting.expression,
      false,
      targeting.criteria,
    );
  }

  // If no expression but we have criteria, normalize all criteria as an AND group
  if (targeting.criteria) {
    const expressions = normalizeCriteria(targeting.criteria);
    if (expressions.length === 0) {
      return null;
    }
    if (expressions.length === 1) {
      return expressions[0];
    }
    return {
      type: 'logical',
      operator: 'and',
      operands: expressions,
    };
  }

  return null;
};
